import React, { useMemo } from 'react';
import { Box } from '@mui/material';

import { RoofContainer, TrussEnvelope } from '@paragon/contract-library';
import { Direction2D, distanceGreaterThan, findConvexHull, Polygon2D } from '@paragon/geometry-library';
import { calculateTrussPolygon2D } from '@paragon/layout';
import { distance2D, formatAsNaturalLanguageList, naturalSortByProperty, notNullish } from '@paragon/utilities';

import { getTrussSolidCalculationDependencies } from '../../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import { getDistanceFormatterSelector } from '../../../store/selectors/getDistanceFormatterSelector';
import { getTrussEnvelopeToTrussGroups } from '../../../store/selectors/trussGroups';
import { layoutColors } from '../../../three/layout/layoutColors';
import { ComponentPreview } from '../../controls/ComponentPreview';
import { PolygonPreview } from '../../controls/PolygonPreview';
import { useAppSelector } from '../../hooks';
import { TooltipContent, TooltipText } from './common';

function measurePolygonDimensionAlongDirection(polygon: Polygon2D, direction: Direction2D): number {
  const coordinatesInDirection = polygon.vertices.map((v) => v.getCoordinate(direction));
  const minCoordinate = Math.min(...coordinatesInDirection);
  const maxCoordinate = Math.max(...coordinatesInDirection);
  return maxCoordinate - minCoordinate;
}

function dimensionTrussPolygonHeights(polygon: Polygon2D | 'error' | null) {
  if (polygon == null || polygon === 'error') return { height: null, shippableHeight: null };
  const convexHull = new Polygon2D(findConvexHull(polygon.vertices));

  const height = measurePolygonDimensionAlongDirection(convexHull, Direction2D.up);

  // Figure out possible rotation angles
  const possibleUpwardDirectionForShipping = convexHull.edges.map(({ direction }) => direction.rotateQuarterTurn());
  // For each angle, measure height by projecting along direction and taking range
  const possibleShippingHeights = possibleUpwardDirectionForShipping.map((direction) =>
    measurePolygonDimensionAlongDirection(convexHull, direction),
  );
  // Calculate minimum of those heights
  const shippableHeight = Math.min(...possibleShippingHeights);

  return distanceGreaterThan(height, shippableHeight) ? { height, shippableHeight } : { height, shippableHeight: null };
}

export function TrussEnvelopeTooltip(props: { guid: string }) {
  const trussEnvelope = useAppSelector<TrussEnvelope | undefined>((state) => state.layout.trussEnvelopes[props.guid]);
  const roofContainer = useAppSelector<RoofContainer | undefined>(
    (state) => state.layout.roofContainers[trussEnvelope?.roofContainerGuid ?? ''],
  );

  const trussEnvelopeToTrussGroups = useAppSelector(getTrussEnvelopeToTrussGroups);
  const trussGroups = useAppSelector((state) => state.trussGroups);
  const trussGroupNames =
    trussEnvelopeToTrussGroups[props.guid]?.map((guid) => trussGroups[guid]?.name).filter(notNullish) ?? [];

  const formatDistance = useAppSelector(getDistanceFormatterSelector);
  const calculationDependencies = useAppSelector(getTrussSolidCalculationDependencies);

  const componentDesigns = useAppSelector((state) => state.componentDesigns.data);
  const componentDesign = trussEnvelope?.componentDesignGuid
    ? componentDesigns[trussEnvelope.componentDesignGuid]
    : null;

  const previewPolygon = useMemo(() => {
    if (componentDesign) return null; // no need to calculate a preview polygon if we have a component design
    if (trussEnvelope == null) return null;

    return calculateTrussPolygon2D(trussEnvelope, calculationDependencies) ?? 'error';
  }, [calculationDependencies, componentDesign, trussEnvelope]);

  const { height, shippableHeight } = dimensionTrussPolygonHeights(previewPolygon);
  const length = useMemo(
    () =>
      trussEnvelope &&
      distance2D(
        trussEnvelope.leftPoint.x,
        trussEnvelope.leftPoint.y,
        trussEnvelope.rightPoint.x,
        trussEnvelope.rightPoint.y,
      ),
    [trussEnvelope],
  );

  if (trussEnvelope == null) {
    console.warn('Unable to find truss envelope for tooltip.');
    return null;
  }

  const lengthWithOverhangs =
    length &&
    trussEnvelope.leftOverhang + trussEnvelope.rightOverhang > 0 &&
    length + trussEnvelope.leftOverhang + trussEnvelope.rightOverhang;

  return (
    <TooltipContent>
      <TooltipText>Truss Envelope {trussEnvelope.name}</TooltipText>
      {roofContainer && <TooltipText sx={{ marginTop: 1 }}>Roof Container {roofContainer.name}</TooltipText>}

      {trussGroupNames.length > 0 && (
        <TooltipText sx={{ marginTop: 1 }}>
          Truss Group{trussGroupNames.length > 1 ? 's' : ''}:{' '}
          {formatAsNaturalLanguageList(
            naturalSortByProperty(trussGroupNames, (name) => name),
            'and',
          )}
        </TooltipText>
      )}

      {!componentDesign && !!length && (
        <TooltipText sx={{ marginTop: 1 }}>
          Length: {formatDistance(length)}
          {lengthWithOverhangs ? ` (${formatDistance(lengthWithOverhangs)} with overhangs)` : ''}
        </TooltipText>
      )}

      {height != null && (
        <TooltipText sx={{ marginTop: 1 }}>
          Height: {formatDistance(height)}
          {shippableHeight != null ? ` (${formatDistance(shippableHeight)} rotated)` : ''}
        </TooltipText>
      )}

      {componentDesign ? (
        <>
          <TooltipText sx={{ marginTop: 1, marginBottom: 1 }}>
            Component Design {componentDesign.component.name}
          </TooltipText>

          <Box sx={{ margin: 1 }}>
            <ComponentPreview
              componentDesignGuid={componentDesign.guid}
              maxWidth={200}
              maxHeight={200}
              color={layoutColors.trussEnvelopes.normal.line}
            />
          </Box>
        </>
      ) : previewPolygon instanceof Polygon2D ? (
        <PolygonPreview
          polygon={previewPolygon}
          maxWidth={200}
          maxHeight={200}
          color={layoutColors.trussEnvelopes.normal.line}
        />
      ) : previewPolygon === 'error' ? (
        <TooltipText sx={{ marginTop: 1 }}>Error generating profile</TooltipText>
      ) : null}
    </TooltipContent>
  );
}
